import type {
  FirmAccountWithContactsSchema,
  FirmAccountlessContactSchema,
  FirmContactForAccountSchema,
  FirmEmployeeSchema,
} from '@liscio/api';
import {
  ACCOUNTLESS_CONTACTS_GROUP_ID,
  type AccountGroup,
  type ContactOption,
  EMPLOYEES_GROUP_ACCOUNT_ID,
} from '@liscio/common';

function getInitials(firstName: string, lastName: string): string {
  const first = firstName?.charAt(0) || '';
  const last = lastName?.charAt(0) || '';
  return (first + last).toUpperCase();
}

function mapContact(contact: FirmContactForAccountSchema): ContactOption {
  const firstName = contact.first_name || '';
  const lastName = contact.last_name || '';
  return {
    id: contact.cpa_user_id ?? contact.id ?? 0,
    contact_id: contact.id ?? undefined,
    uuid: contact.uuid ?? '',
    name: `${firstName} ${lastName}`.trim(),
    email: contact.email ?? undefined,
    type: 'contact',
    initials: getInitials(firstName, lastName),
    cpa_user_id: contact.cpa_user_id ?? undefined,
  };
}

function mapEmployee(employee: FirmEmployeeSchema): ContactOption {
  const firstName = employee.first_name || '';
  const lastName = employee.last_name || '';
  return {
    id: employee.cpa_user_id ?? employee.id ?? 0,
    uuid: employee.uuid ?? '',
    name: `${firstName} ${lastName}`.trim(),
    type: 'employee',
    initials: getInitials(firstName, lastName),
    cpa_user_id: employee.cpa_user_id ?? undefined,
  };
}

function mapAccountlessContact(
  contact: FirmAccountlessContactSchema
): ContactOption {
  const firstName = contact.first_name || '';
  const lastName = contact.last_name || '';
  return {
    id: contact.id,
    contact_id: contact.id,
    uuid: contact.uuid,
    name: `${firstName} ${lastName}`.trim(),
    email: contact.email ?? undefined,
    type: 'contact',
    initials: getInitials(firstName, lastName),
    cpa_user_id: contact.cpa_user_id ?? undefined,
  };
}

export interface MapToAccountGroupsOptions {
  includeEmployees?: boolean;
  accountlessContacts?: FirmAccountlessContactSchema[];
}

export function mapToAccountGroups(
  accounts: FirmAccountWithContactsSchema[],
  employees: FirmEmployeeSchema[],
  options: MapToAccountGroupsOptions = {}
): AccountGroup[] {
  const { includeEmployees = true, accountlessContacts = [] } = options;

  const accountGroups: AccountGroup[] = accounts.map((account) => ({
    account: {
      id: account.id ?? 0,
      uuid: String(account.id ?? ''),
      name: account.name ?? '',
      entityType: account.entity_type ?? undefined,
    },
    contacts: (account.contacts ?? []).map(mapContact),
    contactCount: account.contacts_count ?? account.contacts?.length ?? 0,
  }));

  if (accountlessContacts.length > 0) {
    accountGroups.push({
      account: {
        id: ACCOUNTLESS_CONTACTS_GROUP_ID,
        uuid: 'accountless-contacts',
        name: 'Contacts without Accounts',
        isAccountlessGroup: true,
      },
      contacts: accountlessContacts.map(mapAccountlessContact),
      contactCount: accountlessContacts.length,
    });
  }

  if (includeEmployees && employees.length > 0) {
    accountGroups.push({
      account: {
        id: EMPLOYEES_GROUP_ACCOUNT_ID,
        uuid: 'employees',
        name: 'Employees',
        isEmployeesGroup: true,
      },
      contacts: employees.map(mapEmployee),
      contactCount: employees.length,
    });
  }

  return accountGroups;
}

export function isEmployeesGroupAccountId(accountId: number): boolean {
  return accountId === EMPLOYEES_GROUP_ACCOUNT_ID;
}
